import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { toast } from "react-toastify";
import api from "utils/api";
import Dropdown from "components/CustomDropdown/Dropdown";

function AddBanner() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: "",
    bannerType: "",
    status: "PENDING",
  });
  const [bannerImage, setBannerImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setBannerImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error("Please enter banner title");
      return;
    }
    if (!formData.bannerType) {
      toast.error("Please select banner type");
      return;
    }
    if (!bannerImage) {
      toast.error("Please select banner image");
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append("title", formData.title);
      data.append("bannerType", formData.bannerType);
      data.append("status", formData.status);
      data.append("bannerImage", bannerImage);

      const response = await api.post("/banners", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success(response.data.message || "Banner created successfully");
        navigate("/banner");
      } else {
        toast.error(response.data.message || "Failed to create banner");
      }
    } catch (error) {
      console.error("Error creating banner:", error);
      toast.error(error.response?.data?.message || "Failed to create banner");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            <ArgonBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
              <ArgonTypography variant="h5" fontWeight="medium">
                Add New Banner
              </ArgonTypography>
              <ArgonButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => navigate("/banner")}
              >
                Back to List
              </ArgonButton>
            </ArgonBox>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Banner Image Upload */}
                <Grid item xs={12}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Banner Image *
                  </ArgonTypography>
                  <ArgonBox>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{
                        display: "block",
                        padding: "10px",
                        border: "1px solid #d2d6da",
                        borderRadius: "8px",
                        width: "100%",
                        cursor: "pointer",
                      }}
                    />
                  </ArgonBox>
                  {imagePreview && (
                    <ArgonBox mt={2}>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          maxWidth: "300px",
                          maxHeight: "200px",
                          borderRadius: "8px",
                          border: "1px solid #d2d6da",
                        }}
                      />
                    </ArgonBox>
                  )}
                </Grid>

                {/* Title */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Title *
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter banner title"
                  />
                </Grid>

                {/* Banner Type */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Banner Type *
                  </ArgonTypography>
                  <Dropdown
                    label=""
                    value={formData.bannerType}
                    options={["Promotional", "Educational", "Seasonal"]}
                    onChange={(e) => setFormData({ ...formData, bannerType: e.target.value })}
                  />
                </Grid>

                {/* Status */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Status
                  </ArgonTypography>
                  <Dropdown
                    label=""
                    value={formData.status}
                    options={["ACTIVE", "INACTIVE", "PENDING"]}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  />
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <ArgonBox display="flex" justifyContent="flex-end" gap={2} mt={4}>
                <ArgonButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate("/banner")}
                  disabled={loading}
                >
                  Cancel
                </ArgonButton>
                <ArgonButton type="submit" variant="gradient" color="info" disabled={loading}>
                  {loading ? "Creating..." : "Create Banner"}
                </ArgonButton>
              </ArgonBox>
            </form>
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AddBanner;
